import React, { useEffect, useRef, useState, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Toaster } from 'react-hot-toast';
import { MapClustering } from './MapClustering';
import { MapGeocoding } from './MApGeocoding';
import { MapRouting } from './MapRouting';
import {
  MAPBOX_ACCESS_TOKEN,
  NIGER_BOUNDS,
  NIGER_BOUNDS_WITH_PADDING,
  DEFAULT_CENTER,
  MAP_STYLES,
  MAP_CONFIG,
  CLUSTER_COLORS,
  CLUSTER_SIZES
} from './constants';
import api from './api';

// Configuration du token Mapbox
mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

const MapBoxAccueil = () => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const navigationControlRef = useRef(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [currentStyle, setCurrentStyle] = useState(MAP_STYLES.SATELLITE);
  const [isScrollingDown, setIsScrollingDown] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [depotsData, setDepotsData] = useState(null);
  const [isMapInitialized, setIsMapInitialized] = useState(false);
  const [mapSize, setMapSize] = useState({
    width: '100%',
    height: window.innerHeight
  });
  const [showScrollTop, setShowScrollTop] = useState(false);
  const lastScrollPosition = useRef(0);
  const [showFilters, setShowFilters] = useState(false);
  
  // Références pour les instances des classes
  const clusteringRef = useRef(null);
  const geocodingRef = useRef(null);
  const routingRef = useRef(null);

  // Add new state variables for filtering
  const [regions, setRegions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [communes, setCommunes] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedCommune, setSelectedCommune] = useState('');

  // États pour la distance
  const [selectedPoints, setSelectedPoints] = useState([]);
  const [distance, setDistance] = useState(null);
  const [routeInfo, setRouteInfo] = useState(null);

  // Modifier le gestionnaire de clic
  const handlePointClick = useCallback((event) => {
    console.log('Point click event received:', event);
    
    if (event.type === 'point_selected' && event.coordinates) {
      setSelectedPoints(prev => {
        // Créer une copie du tableau
        const newPoints = [...prev];
        
        // Ajouter les nouvelles coordonnées
        newPoints.push(event.coordinates);
        
        // Ne garder que les deux derniers points
        if (newPoints.length > 2) {
          newPoints.shift();
        }
        
        console.log('New selected points:', newPoints);
        return newPoints;
      });
    }
  }, []);

  // Modifier l'effet de calcul de distance pour ajouter plus de vérifications
  useEffect(() => {
    console.log('Selected points changed:', selectedPoints);
    
    if (selectedPoints.length === 2 && routingRef.current) {
      const [start, end] = selectedPoints;
      
      if (!start || !end) {
        console.error('Invalid coordinates:', { start, end });
        return;
      }

      console.log('Calculating route between:', start, end);
      
      try {
        routingRef.current.calculateDistance(
          start,
          end,
          (info) => {
            console.log('Route info received:', info);
            if (info) {
              setRouteInfo(info);
            } else {
              console.error('No route info received');
            }
          }
        );
      } catch (error) {
        console.error('Error calculating route:', error);
      }
    }
  }, [selectedPoints]);

  // Effet d'initialisation des composants
  useEffect(() => {
    if (!map.current || !depotsData || !isMapInitialized) return;

    const initializeMapComponents = () => {
      console.log('Initializing map components');
      
      // Nettoyer les composants existants
      if (clusteringRef.current) {
        clusteringRef.current.removeClustering();
      }
      if (geocodingRef.current) {
        geocodingRef.current.remove();
      }
      if (routingRef.current) {
        routingRef.current.clearRoute();
      }

      // Initialiser dans le bon ordre
      routingRef.current = new MapRouting(map.current);
      console.log('Routing initialized');
      
      clusteringRef.current = new MapClustering(map.current);
      clusteringRef.current.setPointClickHandler((e) => {
        console.log('Clustering click handler called');
        handlePointClick(e);
      });
      clusteringRef.current.initializeClustering(depotsData);
      console.log('Clustering initialized');
      
      geocodingRef.current = new MapGeocoding(map.current);
      geocodingRef.current.initialize(isMobile, depotsData.features);
    };

    initializeMapComponents();
  }, [depotsData, isMapInitialized, currentStyle, isMobile, handlePointClick]);

  // Fonction pour adapter la carte au format mobile
  const adaptMapToMobile = useCallback(() => {
    if (!map.current) return;
    
    const width = window.innerWidth;
    const height = window.innerHeight;
    const isMobileView = width <= 768;
    
    setIsMobile(isMobileView);
    
    // Mettre à jour la taille du conteneur de la carte
    if (mapContainer.current) {
      mapContainer.current.style.height = `${isMobileView ? height - 64 : height}px`;
      mapContainer.current.style.width = '100%';
    }

    // Forcer le rafraîchissement de la carte
    map.current.resize();

    // Ajuster le zoom et les limites pour le mobile
    if (isMobileView) {
      // Ajuster les limites de la carte pour le Niger
      map.current.fitBounds([
        [0.16, 11.69], // Sud-ouest du Niger
        [15.99, 23.52] // Nord-est du Niger
      ], {
        padding: { top: 50, bottom: 50, left: 20, right: 20 },
        maxZoom: 5.2,  // Limiter le zoom pour garder une vue d'ensemble
        duration: 0    // Pas d'animation pour l'ajustement initial
      });
    }

    // Ajuster les contrôles sur mobile
    const geocoder = document.querySelector('.mapboxgl-ctrl-geocoder');
    if (geocoder && isMobileView) {
      geocoder.style.width = '200px';
      geocoder.style.position = 'absolute';
      geocoder.style.bottom = '20px';
      geocoder.style.left = '10px';
    }
  }, []);

  // Initialisation de la carte
  useEffect(() => {
    if (map.current) return;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: `mapbox://styles/mapbox/${currentStyle}`,
      center: [8.5, 17.5], // Centre du Niger
      zoom: window.innerWidth <= 768 ? 4.5 : 5.5, // Zoom initial différent pour mobile
      maxBounds: [
        [-2, 8],    // Sud-ouest avec marge
        [20, 28]    // Nord-est avec marge
      ],
      minZoom: 4.5, // Zoom minimum plus petit pour mobile
      maxZoom: 16,
      dragRotate: false,
      pitchWithRotate: false,
      pitch: 0,
      bearing: 0,
      renderWorldCopies: false
    });

    map.current.touchZoomRotate.disableRotation();
    map.current.dragRotate.disable();

    // Ajout du contrôle de navigation
    navigationControlRef.current = new mapboxgl.NavigationControl({
      showCompass: false,
      visualizePitch: false
    });
    map.current.addControl(navigationControlRef.current, 'top-right');

    // Ajuster la vue initiale
    map.current.fitBounds(NIGER_BOUNDS, {
      padding: 20,
      duration: 0
    });

    map.current.on('load', () => {
      setIsMapInitialized(true);
      adaptMapToMobile();
    });

    return () => {
      if (navigationControlRef.current) {
        map.current.removeControl(navigationControlRef.current);
        navigationControlRef.current = null;
      }
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [adaptMapToMobile, currentStyle]);

  // Gestion de la responsivité
  useEffect(() => {
    // Adapter la carte immédiatement au montage
    adaptMapToMobile();

    const handleResize = () => {
      adaptMapToMobile();
    };

    const handleOrientationChange = () => {
      // Attendre que le changement d'orientation soit terminé
      setTimeout(adaptMapToMobile, 100);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientationChange);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, [adaptMapToMobile]);

  // Chargement des données avec retry
  useEffect(() => {
    const fetchDepotsData = async (retryCount = 0) => {
      const maxRetries = 3;
      const retryDelay = 1000; // 1 seconde

      try {
        setIsLoading(true);
        const response = await api.get('/collect/data-map/');
        setDepotsData({
          type: 'FeatureCollection',
          features: response.data.features
        });
        setError(null);
      } catch (error) {
        console.error('Erreur lors de la récupération des données:', error);
        if (retryCount < maxRetries) {
          setTimeout(() => {
            fetchDepotsData(retryCount + 1);
          }, retryDelay * (retryCount + 1));
        } else {
          setError('Erreur lors du chargement des données');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchDepotsData();
  }, []);

  // Définir setupEventListeners comme une fonction du composant
  const setupEventListeners = () => {
    if (!map.current) return;

    // Clic sur les clusters
    map.current.on('click', 'clusters', (e) => {
      const features = map.current.queryRenderedFeatures(e.point, { layers: ['clusters'] });
      const clusterId = features[0].properties.cluster_id;

      map.current.getSource('depots').getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err) return;
        map.current.easeTo({
          center: features[0].geometry.coordinates,
          zoom: zoom
        });
      });
    });

    // Clic sur les points
    map.current.on('click', 'unclustered-point', (e) => {
      if (!e.features || !e.features.length) return;
      
      const coordinates = e.features[0].geometry.coordinates;
      if (handlePointClick) {
        handlePointClick({
          type: 'point_selected',
          coordinates: coordinates
        });
      }
    });

    // Effets de survol
    map.current.on('mouseenter', 'clusters', () => {
      map.current.getCanvas().style.cursor = 'pointer';
    });

    map.current.on('mouseleave', 'clusters', () => {
      map.current.getCanvas().style.cursor = '';
    });

    map.current.on('mouseenter', 'unclustered-point', () => {
      map.current.getCanvas().style.cursor = 'pointer';
    });

    map.current.on('mouseleave', 'unclustered-point', () => {
      map.current.getCanvas().style.cursor = '';
    });
  };

  // Ajoutez cette fonction de réinitialisation des données
  const reloadDataLayers = () => {
    if (!map.current || !depotsData) return;

    // Ajouter la source si elle n'existe pas
    if (!map.current.getSource('depots')) {
      map.current.addSource('depots', {
        type: 'geojson',
        data: depotsData,
        cluster: true,
        clusterMaxZoom: 14,
        clusterRadius: 50
      });

      // Ajouter les couches
      map.current.addLayer({
        id: 'clusters',
        type: 'circle',
        source: 'depots',
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': [
            'step',
            ['get', 'point_count'],
            CLUSTER_COLORS.LOW,
            100,
            CLUSTER_COLORS.MEDIUM,
            750,
            CLUSTER_COLORS.HIGH
          ],
          'circle-radius': [
            'step',
            ['get', 'point_count'],
            CLUSTER_SIZES.LOW,
            100,
            CLUSTER_SIZES.MEDIUM,
            750,
            CLUSTER_SIZES.HIGH
          ]
        }
      });

      map.current.addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: 'depots',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 12
        }
      });

      map.current.addLayer({
        id: 'unclustered-point',
        type: 'circle',
        source: 'depots',
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color': [
            'match',
            ['get', 'status'],
            'Reconnu', '#10B981',
            'Non Reconnu', '#F97316',
            '#059669'
          ],
          'circle-radius': 8
        }
      });
    }
  };

  // Add filtering function
  const getFilteredData = useCallback(() => {
    if (!depotsData) return null;

    const filteredFeatures = depotsData.features.filter(feature => {
      const matchRegion = !selectedRegion || feature.properties.region === selectedRegion;
      const matchDepartment = !selectedDepartment || feature.properties.departement === selectedDepartment;
      const matchCommune = !selectedCommune || feature.properties.commune === selectedCommune;
      return matchRegion && matchDepartment && matchCommune;
    });

    return {
      type: "FeatureCollection",
      features: filteredFeatures
    };
  }, [depotsData, selectedRegion, selectedDepartment, selectedCommune]);

  // Modifiez la fonction changeMapStyle
  const changeMapStyle = useCallback((styleName) => {
    if (!map.current) return;
  
    const currentZoom = map.current.getZoom();
    const currentCenter = map.current.getCenter();
    const currentPitch = map.current.getPitch();
    const currentBearing = map.current.getBearing();
  
    map.current.setStyle(`mapbox://styles/mapbox/${styleName}`);
  
    map.current.once('style.load', () => {
      map.current.setCenter(currentCenter);
      map.current.setZoom(currentZoom);
      map.current.setPitch(currentPitch);
      map.current.setBearing(currentBearing);
  
      const filteredData = getFilteredData();
  
      if (filteredData) {
        // Réinitialiser clustering avec les données filtrées
        if (clusteringRef.current) {
          clusteringRef.current.removeClustering();
        }
  
        clusteringRef.current = new MapClustering(map.current);
        clusteringRef.current.setPointClickHandler(handlePointClick);
        clusteringRef.current.initializeClustering(filteredData);
  
        // Réinitialiser geocoding
        if (geocodingRef.current) {
          geocodingRef.current.remove();
        }
        geocodingRef.current = new MapGeocoding(map.current);
        geocodingRef.current.initialize(isMobile, filteredData.features);
  
        // Réinitialiser routing
        if (routingRef.current) {
          routingRef.current.clearRoute();
        }
        routingRef.current = new MapRouting(map.current);
      }
  
      setupEventListeners();
    });
  
    setCurrentStyle(styleName);
  }, [getFilteredData, handlePointClick, isMobile, setupEventListeners]);
  

  useEffect(() => {
    const handleScroll = () => {
      const currentScroll = window.pageYOffset;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      
      // Déterminer la direction du défilement
      setIsScrollingDown(
        currentScroll < documentHeight - windowHeight - 100
      );
      
      lastScrollPosition.current = currentScroll;
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToPosition = () => {
    if (isScrollingDown) {
      window.scrollTo({
        top: document.documentElement.scrollHeight,
        behavior: 'smooth'
      });
    } else {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  // Add new effect for extracting unique values
  useEffect(() => {
    if (!depotsData?.features) return;

    const uniqueRegions = [...new Set(depotsData.features.map(f => f.properties.region))].sort();
    const uniqueDepartments = [...new Set(depotsData.features.map(f => f.properties.departement))].filter(Boolean).sort();
    const uniqueCommunes = [...new Set(depotsData.features.map(f => f.properties.commune))].filter(Boolean).sort();

    setRegions(uniqueRegions);
    setDepartments(uniqueDepartments);
    setCommunes(uniqueCommunes);
  }, [depotsData]);

  // Add effect for updating clusters
  useEffect(() => {
    if (!map.current || !depotsData || !isMapInitialized) return;
    
    const filteredData = getFilteredData();
    if (filteredData) {
      clusteringRef.current?.updateClusters(filteredData);
    }
  }, [selectedRegion, selectedDepartment, selectedCommune, depotsData, isMapInitialized]);

  // Ajouter cette nouvelle fonction pour ajuster la vue
  const adjustMapView = useCallback((filteredData) => {
    if (!map.current || !filteredData?.features?.length) return;

    // Calculer les bounds pour les points filtrés
    const bounds = new mapboxgl.LngLatBounds();
    filteredData.features.forEach(feature => {
      bounds.extend(feature.geometry.coordinates);
    });

    // Ajuster la vue avec animation
    map.current.fitBounds(bounds, {
      padding: { top: 100, bottom: 100, left: 100, right: 100 },
      maxZoom: 12, // Limiter le zoom pour voir les clusters
      duration: 1000 // Animation d'une seconde
    });
  }, []);

  // Fonction pour gérer les changements de sélection
  const handleSelectionChange = (setter, value, resetCommune = true) => {
    setter(value);
    
    if (resetCommune) {
      setSelectedCommune('');
    }
    
    // Toujours cacher les filtres après une sélection, quel que soit le type d'écran
    setShowFilters(false);
  };

  // Ajouter ces nouvelles fonctions de comptage
  const countItemsInRegion = useCallback((region) => {
    if (!depotsData?.features) return 0;
    return depotsData.features.filter(f => f.properties.region === region).length;
  }, [depotsData]);

  const countItemsInDepartment = useCallback((department) => {
    if (!depotsData?.features) return 0;
    return depotsData.features.filter(f => 
      f.properties.departement === department &&
      (!selectedRegion || f.properties.region === selectedRegion)
    ).length;
  }, [depotsData, selectedRegion]);

  const countItemsInCommune = useCallback((commune) => {
    if (!depotsData?.features) return 0;
    return depotsData.features.filter(f => 
      f.properties.commune === commune &&
      (!selectedRegion || f.properties.region === selectedRegion) &&
      (!selectedDepartment || f.properties.departement === selectedDepartment)
    ).length;
  }, [depotsData, selectedRegion, selectedDepartment]);

  useEffect(() => {
    if (map.current && routingRef.current) {
      window.mapInstance = map.current;
      window.routingInstance = routingRef.current;

      window.initializeRoute = (coordinates) => {
        if (routingRef.current) {
          routingRef.current.initializeRoute(coordinates);
        } else {
          console.error("Routing instance not ready");
        }
      };
    }

    return () => {
      window.mapInstance = null;
      window.routingInstance = null;
      window.initializeRoute = null;
    };
  }, [isMapInitialized, routingRef.current, depotsData]);

  // Add this new effect to handle point selection and distance calculation
  useEffect(() => {
    if (!map.current || !isMapInitialized) return;

    const handleClick = (e) => {
      handlePointClick(e);
    };

    // Add click handler for points
    map.current.on('click', 'unclustered-point', handleClick);

    return () => {
      if (map.current) {
        map.current.off('click', 'unclustered-point', handleClick);
      }
    };
  }, [isMapInitialized, handlePointClick]);

  const resetMapView = useCallback(() => {
    if (!map.current || !depotsData) return;

    // Réinitialiser le style initial
    const initialStyle = MAP_STYLES.SATELLITE;
    setCurrentStyle(initialStyle);
    map.current.setStyle(`mapbox://styles/mapbox/${initialStyle}`);

    map.current.once('style.load', () => {
      // Réinitialiser la vue initiale
      map.current.fitBounds(NIGER_BOUNDS, {
        padding: 20,
        duration: 1000
      });

      // Réinitialiser les filtres
      setSelectedRegion('');
      setSelectedDepartment('');
      setSelectedCommune('');

      // Réinitialiser les clusters avec les données initiales
      if (clusteringRef.current) {
        clusteringRef.current.removeClustering();
      }
      clusteringRef.current = new MapClustering(map.current);
      clusteringRef.current.setPointClickHandler(handlePointClick);
      clusteringRef.current.initializeClustering(depotsData);

      // Réinitialiser le geocoding
      if (geocodingRef.current) {
        geocodingRef.current.remove();
      }
      geocodingRef.current = new MapGeocoding(map.current);
      geocodingRef.current.initialize(isMobile, depotsData.features);

      // Réinitialiser le routing
      if (routingRef.current) {
        routingRef.current.clearRoute();
      }
      routingRef.current = new MapRouting(map.current);

      setupEventListeners();
    });
  }, [depotsData, handlePointClick, isMobile, setupEventListeners]);

  return (
    <div className="relative w-full h-screen">
      {/* Indicateur des filtres actifs avec design amélioré */}
      {(selectedRegion || selectedDepartment || selectedCommune) && (
        <div className="absolute top-20 left-1/2 transform -translate-x-1/2 z-20">
          <div className="bg-white/90 backdrop-blur-sm px-4 py-2.5 rounded-xl shadow-lg 
            text-sm flex items-center gap-2 border border-gray-100 transition-all duration-300
            hover:bg-white">
            <span className="text-gray-700 font-medium">
              {[
                selectedRegion && (
                  <span className="inline-flex items-center">
                    <span className="text-emerald-600">Région:</span>
                    <span className="ml-1">{selectedRegion}</span>
                  </span>
                ),
                selectedDepartment && (
                  <span className="inline-flex items-center">
                    <span className="text-emerald-600">Département:</span>
                    <span className="ml-1">{selectedDepartment}</span>
                  </span>
                ),
                selectedCommune && (
                  <span className="inline-flex items-center">
                    <span className="text-emerald-600">Commune:</span>
                    <span className="ml-1">{selectedCommune}</span>
                  </span>
                )
              ].filter(Boolean).map((item, index, array) => (
                <span key={index}>
                  {item}
                  {index < array.length - 1 && (
                    <span className="mx-2 text-gray-400">•</span>
                  )}
                </span>
              ))}
            </span>
            <button
              onClick={() => {
                setSelectedRegion('');
                setSelectedDepartment('');
                setSelectedCommune('');
                if (window.innerWidth <= 768) {
                  setShowFilters(false);
                }
              }}
              className="ml-2 p-1 rounded-full hover:bg-red-50 text-red-500 
                hover:text-red-600 transition-colors duration-200"
              aria-label="Réinitialiser les filtres"
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-4 w-4" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M6 18L18 6M6 6l12 12" 
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Loader */}
      {isLoading && (
        <div className="absolute inset-0 bg-white bg-opacity-75 z-50 flex items-center justify-center">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
            <p className="mt-4 text-sm text-gray-600">Chargement des données...</p>
          </div>
        </div>
      )}

      {/* Message d'erreur */}
      {error && (
        <div className="absolute top-4 left-4 right-4 z-50 bg-red-100 text-red-700 p-4 rounded-lg shadow-md">
          {error}
        </div>
      )}

      <div 
        ref={mapContainer} 
        className="absolute inset-0 w-full h-full"
        style={{
          height: isMobile ? 'calc(100vh - 64px)' : '100vh'
        }}
      />
      
      {/* Bouton des filtres repositionné */}
      <button
        onClick={() => setShowFilters(prev => !prev)}
        className="filter-toggle-button absolute top-[100px] left-4 z-50 bg-white rounded-full shadow-md p-2 hover:bg-gray-100 transition-colors"
        aria-label="Afficher/Cacher les filtres"
      >
        🔍
      </button>

      {/* Panneau de filtres avec masquage complet sur mobile */}
      <div 
        className={`absolute top-[150px] left-4 transition-all duration-300 transform
          filter-container 
          ${showFilters ? 'translate-x-0 opacity-100' : '-translate-x-[110%] opacity-0'}`}
      >
        <div className="flex flex-col gap-2 w-full md:w-[250px]">
          <div className="bg-white p-3 rounded-lg shadow-md w-full">
            <div className="space-y-2">
              {/* Sélecteur de région avec recherche */}
              <div className="relative w-full">
                <select
                  value={selectedRegion}
                  onChange={(e) => handleSelectionChange(setSelectedRegion, e.target.value)}
                  className="w-full px-2 py-1.5 rounded-md border border-gray-300 
                    focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm
                    appearance-none bg-transparent"
                >
                  <option value="">Toutes les régions ({depotsData?.features?.length || 0})</option>
                  {regions.map(region => (
                    <option key={region} value={region}>
                      {region} ({countItemsInRegion(region)})
                    </option>
                  ))}
                </select>
              </div>

              {/* Sélecteur de département avec recherche */}
              <div className="relative w-full">
                <select
                  value={selectedDepartment}
                  onChange={(e) => handleSelectionChange(setSelectedDepartment, e.target.value, false)}
                  className="w-full px-2 py-1.5 rounded-md border border-gray-300 
                    focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm
                    appearance-none bg-transparent"
                >
                  <option value="">
                    Tous les départements ({depotsData?.features?.filter(f => 
                      !selectedRegion || f.properties.region === selectedRegion
                    ).length || 0})
                  </option>
                  {departments
                    .filter(dept => !selectedRegion || 
                      depotsData.features.some(f => 
                        f.properties.region === selectedRegion && 
                        f.properties.departement === dept
                      ))
                    .map(department => (
                      <option key={department} value={department}>
                        {department} ({countItemsInDepartment(department)})
                      </option>
                    ))}
                </select>
              </div>

              {/* Sélecteur de commune avec recherche */}
              <div className="relative w-full">
                <select
                  value={selectedCommune}
                  onChange={(e) => handleSelectionChange(setSelectedCommune, e.target.value, false)}
                  className="w-full px-2 py-1.5 rounded-md border border-gray-300 
                    focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm
                    appearance-none bg-transparent"
                >
                  <option value="">
                    Toutes les communes ({depotsData?.features?.filter(f => 
                      (!selectedRegion || f.properties.region === selectedRegion) &&
                      (!selectedDepartment || f.properties.departement === selectedDepartment)
                    ).length || 0})
                  </option>
                  {communes
                    .filter(com => 
                      (!selectedRegion || 
                        depotsData.features.some(f => 
                          f.properties.region === selectedRegion && 
                          f.properties.commune === com
                      )) &&
                      (!selectedDepartment || 
                        depotsData.features.some(f => 
                          f.properties.departement === selectedDepartment && 
                          f.properties.commune === com
                      ))
                    )
                    .map(commune => (
                      <option key={commune} value={commune}>
                        {commune} ({countItemsInCommune(commune)})
                      </option>
                    ))}
                </select>
              </div>

              {/* Bouton de réinitialisation */}
              {(selectedRegion || selectedDepartment || selectedCommune) && (
                <button
                  onClick={() => {
                    setSelectedRegion('');
                    setSelectedDepartment('');
                    setSelectedCommune('');
                    if (window.innerWidth <= 768) {
                      setShowFilters(false);
                    }
                  }}
                  className="w-full px-3 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 
                    transition-colors duration-200 text-sm"
                >
                  Réinitialiser les filtres
                </button>
              )}
            </div>
          </div>

          {/* Style de carte */}
          <div className="bg-white p-2 rounded-lg shadow-md w-full">
            <div className="relative">
              <select
                value={currentStyle}
                onChange={(e) => changeMapStyle(e.target.value)}  // Utiliser changeMapStyle au lieu de setCurrentStyle
                className="w-full px-3 py-2 rounded-md border border-gray-300 
                  focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm
                  appearance-none bg-transparent"
              >
                <option value="satellite-streets-v12">Vue Satellite</option>
                <option value="streets-v12">Vue Rues</option>
                <option value="light-v11">Vue Claire</option>
                <option value="dark-v11">Vue Sombre</option>
                <option value="outdoors-v12">Vue Terrain</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Bouton de défilement */}
      <button
        onClick={scrollToPosition}
        className="fixed right-4 bottom-4 z-50 bg-white rounded-full p-3 shadow-lg
          hover:bg-gray-100 transition-all duration-300 transform
          focus:outline-none focus:ring-2 focus:ring-emerald-500
          scroll-button-bounce"
        aria-label={isScrollingDown ? "Défiler vers le bas" : "Défiler vers le haut"}
      >
        <svg
          className={`w-6 h-6 text-emerald-600 transition-transform duration-300
            ${isScrollingDown ? 'rotate-0' : 'rotate-180'}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 14l-7 7m0 0l-7-7m7 7V3"
          />
        </svg>
      </button>

      {/* Affichage de la distance avec plus de visibilité */}
      {(selectedPoints.length > 0 || routeInfo) && (
        <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 z-20">
          <div className="bg-white px-4 py-3 rounded-xl shadow-lg 
            border border-emerald-100 text-sm">
            {routeInfo ? (
              <div className="space-y-2">
                <h3 className="font-semibold text-gray-700 mb-2">Estimations:</h3>
                <div className="grid gap-2">
                  {routeInfo.driving && (
                    <div className="flex items-center gap-2">
                      <span className="text-blue-600">🚗</span>
                      <span>En voiture: {routeInfo.driving.distance} km ({routeInfo.driving.duration} min)</span>
                    </div>
                  )}
                  {routeInfo.walking && (
                    <div className="flex items-center gap-2">
                      <span className="text-green-600">🚶</span>
                      <span>À pied: {routeInfo.walking.distance} km ({routeInfo.walking.duration} min)</span>
                    </div>
                  )}
                  {routeInfo.cycling && (
                    <div className="flex items-center gap-2">
                      <span className="text-orange-600">🚲</span>
                      <span>À vélo: {routeInfo.cycling.distance} km ({routeInfo.cycling.duration} min)</span>
                    </div>
                  )}
                  <button
                    onClick={() => {
                      setSelectedPoints([]);
                      setRouteInfo(null);
                      routingRef.current?.clearRoute();
                    }}
                    className="px-2 py-1 mt-2 text-xs bg-red-50 text-red-600 rounded-md 
                      hover:bg-red-100 transition-colors duration-200"
                  >
                    Réinitialiser
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-gray-600">
                  {selectedPoints.length === 1 
                    ? "Sélectionnez le deuxième point" 
                    : "Sélectionnez deux points pour mesurer la distance"}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      <button
        onClick={() => window.location.reload()}
        className="absolute top-28 right-4 z-50 bg-white rounded-full shadow-md p-2 hover:bg-gray-100 transition-colors"
        aria-label="Recharger la carte"
      >
        🔄
      </button>
    </div>
  );
};

export default MapBoxAccueil;